// ConnectionLines.js - Updated with Row Connections (Dashed Lines)
import React from 'react';
import colors, { white } from '../ColorConfig/colorconfig';

const { gray, primary } = colors;

const ConnectionLines = ({ 
  isSectionInView, 
  animationStarted, 
  processSteps,
  visibleSteps = [],
  shouldAnimateOut = false,
  isMobile = false 
}) => {  
  if (isMobile) {
    return <MobileConnectionLines 
      isSectionInView={isSectionInView}
      animationStarted={animationStarted}
      processSteps={processSteps}
      visibleSteps={visibleSteps}
      shouldAnimateOut={shouldAnimateOut}
    />;
  }

    const isConnectionVisible = (fromStepIndex, toStepIndex) => {
    // Connection is visible only if both connected steps are visible
    return visibleSteps.includes(fromStepIndex) && visibleSteps.includes(toStepIndex);
  };

  // Helper function to get connection opacity with smooth transitions
  const getConnectionOpacity = (fromStepIndex, toStepIndex) => {
    if (!isSectionInView) return 0;
    
    const bothStepsVisible = isConnectionVisible(fromStepIndex, toStepIndex);
    
    if (shouldAnimateOut) {
      // When hiding, fade out based on step visibility
      return bothStepsVisible ? 0.7 : 0;
    }
    
    // When showing, fade in based on step visibility
    return bothStepsVisible ? 0.7 : 0;
  };

  // Calculate delay for connection animations based on connected steps
  const getConnectionDelay = (fromStepIndex, toStepIndex) => {
    if (shouldAnimateOut) {
      // When hiding, use the delay of the later step (higher index)
      return Math.max(fromStepIndex, toStepIndex) * 0.15;
    }
    // When showing, use the delay of the later step
    return Math.max(fromStepIndex, toStepIndex) * 0.2;
  };

  return (
    <svg 
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 1,
        pointerEvents: 'none'
      }}
      viewBox="0 0 1400 400"
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        <filter id="glow">
          <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
          <feMerge> 
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
        
        <linearGradient id="desktopLineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={gray} stopOpacity="0.5" />
          <stop offset="50%" stopColor={gray} stopOpacity="0.9" />
          <stop offset="100%" stopColor={gray} stopOpacity="0.5" />
        </linearGradient>
      </defs>

      {/* Step 1 to Step 2 */}
      <g style={{
        opacity: getConnectionOpacity(0, 1),
        transition: `opacity 0.4s ease-in-out ${getConnectionDelay(0, 1)}s`,
      }}>
        <line x1="150" y1="280" x2="150" y2="320" stroke={gray} strokeWidth="1" />
        <line x1="150" y1="320" x2="250" y2="320" stroke={gray} strokeWidth="1" />
        <line x1="250" y1="320" x2="250" y2="200" stroke={gray} strokeWidth="1" />
      </g>

      {/* Step 2 to Step 3 */}
      <g style={{
        opacity: getConnectionOpacity(1, 2),
        transition: `opacity 0.4s ease-in-out ${getConnectionDelay(1, 2)}s`,
      }}>
        <line x1="250" y1="200" x2="250" y2="160" stroke={gray} strokeWidth="1" />
        <line x1="250" y1="160" x2="450" y2="160" stroke={gray} strokeWidth="1" />
        <line x1="450" y1="160" x2="450" y2="280" stroke={gray} strokeWidth="1" />
      </g>

      {/* Step 3 to Step 4 */}
      <g style={{
        opacity: getConnectionOpacity(2, 3),
        transition: `opacity 0.4s ease-in-out ${getConnectionDelay(2, 3)}s`,
      }}>
        <line x1="450" y1="280" x2="450" y2="320" stroke={gray} strokeWidth="1" />
        <line x1="450" y1="320" x2="650" y2="320" stroke={gray} strokeWidth="1" />
        <line x1="650" y1="320" x2="650" y2="200" stroke={gray} strokeWidth="1" />
      </g>

      {/* Step 4 to Step 5 */}
      <g style={{
        opacity: getConnectionOpacity(3, 4),
        transition: `opacity 0.4s ease-in-out ${getConnectionDelay(3, 4)}s`,
      }}>
        <line x1="650" y1="200" x2="650" y2="160" stroke={gray} strokeWidth="1" />
        <line x1="650" y1="160" x2="850" y2="160" stroke={gray} strokeWidth="1" />
        <line x1="850" y1="160" x2="850" y2="280" stroke={gray} strokeWidth="1" />
      </g>

      {/* Step 5 to Step 6 */}
      <g style={{
        opacity: getConnectionOpacity(4, 5),
        transition: `opacity 0.4s ease-in-out ${getConnectionDelay(4, 5)}s`,
      }}>
        <line x1="850" y1="280" x2="850" y2="320" stroke={gray} strokeWidth="1" />
        <line x1="850" y1="320" x2="1050" y2="320" stroke={gray} strokeWidth="1" />
        <line x1="1050" y1="320" x2="1050" y2="200" stroke={gray} strokeWidth="1" />
      </g>

      {/* Step 6 to Step 7 */}
      <g style={{
        opacity: getConnectionOpacity(5, 6),
        transition: `opacity 0.4s ease-in-out ${getConnectionDelay(5, 6)}s`,
      }}>
        <line x1="1050" y1="200" x2="1050" y2="160" stroke={gray} strokeWidth="1" />
        <line x1="1050" y1="160" x2="1250" y2="160" stroke={gray} strokeWidth="1" />
        <line x1="1250" y1="160" x2="1250" y2="280" stroke={gray} strokeWidth="1" />
      </g>

      {/* Animated flowing bubble - only show when all steps are visible */}
      {animationStarted && !shouldAnimateOut && visibleSteps.length === processSteps.length && (
        <circle
          r="6"
          fill={primary}
          style={{
            opacity: 0.5
          }}
        >
          <animateMotion
            dur="15s"
            repeatCount="indefinite"
            path="M 150 280 
                 L 150 320 
                 L 250 320 
                 L 250 200 
                 L 250 160 
                 L 450 160 
                 L 450 280 
                 L 450 320 
                 L 650 320 
                 L 650 200 
                 L 650 160 
                 L 850 160 
                 L 850 280 
                 L 850 320 
                 L 1050 320 
                 L 1050 200 
                 L 1050 160 
                 L 1250 160 
                 L 1250 280"
          />
          <animate
            attributeName="r"
            values="4;8;4"
            dur="2s"
            repeatCount="indefinite"
          />
        </circle>
      )}
   
    </svg>
  );
};

// Mobile Connection Lines Component - UPDATED with row connections (dashed lines)
const MobileConnectionLines = ({ 
  isSectionInView, 
  animationStarted, 
  processSteps, 
  visibleSteps = [],
  shouldAnimateOut = false 
}) => {
  // Card dimensions based on typical mobile card sizing
  const cardHeight = 100;
  const rowGap = 30;
  const rowHeight = cardHeight + rowGap;
  
  // Container dimensions
  const containerWidth = 100;
  const leftCardCenter = 22.5;
  const rightCardCenter = 77.5;
  const centerCardX = 50; // Center position for last step
  
  // Calculate total height needed for all rows
  const totalRows = Math.ceil(processSteps.length / 2);
  const totalHeight = (rowHeight * totalRows) + 40;
  
  // Calculate all step positions with centered last step
  const stepPositions = processSteps.map((step, index) => {
    const isLastStep = index === processSteps.length - 1;
    
    if (isLastStep) {
      // Center the last step
      const rowIndex = Math.floor((processSteps.length - 1) / 2);
      const xPos = centerCardX;
      const yPos = (rowIndex * rowHeight) + (cardHeight / 2) + 20;
      
      return {
        x: xPos,
        y: yPos,
        rowIndex,
        isLeftStep: false,
        isCentered: true,
        step,
        isVisible: visibleSteps.includes(index),
        index
      };
    } else {
      // Regular steps in pairs
      const rowIndex = Math.floor(index / 2);
      const isLeftStep = index % 2 === 0;
      const xPos = isLeftStep ? leftCardCenter : rightCardCenter;
      const yPos = (rowIndex * rowHeight) + (cardHeight / 2) + 20;
      
      return {
        x: xPos,
        y: yPos,
        rowIndex,
        isLeftStep,
        isCentered: false,
        step,
        isVisible: visibleSteps.includes(index),
        index
      };
    }
  });

  // Generate connection path segments with visibility tracking
  const generateConnectionPathSegments = () => {
    if (stepPositions.length === 0) return [];
    
    const segments = [];
    
    for (let i = 1; i < stepPositions.length; i++) {
      const current = stepPositions[i];
      const previous = stepPositions[i - 1];
      
      // Check if both steps are visible for this segment
      const isSegmentVisible = current.isVisible && previous.isVisible;
      
      if (current.rowIndex === previous.rowIndex) {
        // Same row - direct horizontal connection
        segments.push({
          type: 'line',
          d: `M ${previous.x} ${previous.y} L ${current.x} ${current.y}`,
          fromIndex: previous.index,
          toIndex: current.index,
          isVisible: isSegmentVisible
        });
      } else {
        // Different row - create elegant curved connection
        const verticalGap = current.y - previous.y;
        const horizontalGap = current.x - previous.x;
        
        // Create S-curve connection
        const d = `M ${previous.x} ${previous.y} 
                 C ${previous.x} ${previous.y + (verticalGap * 0.3)}, 
                   ${previous.x + (horizontalGap * 0.3)} ${previous.y + (verticalGap * 0.5)}, 
                   ${previous.x + (horizontalGap * 0.5)} ${previous.y + (verticalGap * 0.5)}
                 C ${previous.x + (horizontalGap * 0.7)} ${previous.y + (verticalGap * 0.5)}, 
                   ${current.x} ${current.y - (verticalGap * 0.3)}, 
                   ${current.x} ${current.y}`;
        
        segments.push({
          type: 'curve',
          d,
          fromIndex: previous.index,
          toIndex: current.index,
          isVisible: isSegmentVisible
        });
      }
    }
    
    return segments;
  };

  // Generate ROW connection lines (horizontal dashed lines between steps in same row)
  const generateRowConnections = () => {
    const rowConnections = [];
    
    // Group steps by row
    const stepsByRow = {};
    stepPositions.forEach(position => {
      if (!stepsByRow[position.rowIndex]) {
        stepsByRow[position.rowIndex] = [];
      }
      stepsByRow[position.rowIndex].push(position);
    });
    
    // Create horizontal connections for each row that has both left and right steps
    Object.keys(stepsByRow).forEach(rowIndex => {
      const rowSteps = stepsByRow[rowIndex];
      
      // Only create row connection if there are exactly 2 steps in this row (left and right)
      if (rowSteps.length === 2) {
        const leftStep = rowSteps.find(step => step.isLeftStep);
        const rightStep = rowSteps.find(step => !step.isLeftStep);
        
        if (leftStep && rightStep) {
          // Check if both steps in the row are visible
          const isRowVisible = leftStep.isVisible && rightStep.isVisible;
          
          rowConnections.push({
            type: 'row',
            d: `M ${leftStep.x} ${leftStep.y} L ${rightStep.x} ${rightStep.y}`,
            leftIndex: leftStep.index,
            rightIndex: rightStep.index,
            rowIndex: parseInt(rowIndex),
            isVisible: isRowVisible
          });
        }
      }
    });
    
    return rowConnections;
  };

  const pathSegments = generateConnectionPathSegments();
  const rowConnections = generateRowConnections();
  
const getStepAnimationDelay = (index, isHiding = false) => {
  if (isHiding) {
    return (processSteps.length - 1 - index) * 0.08; // Faster hiding
  }
  return index * 0.1; // 100ms delay - faster showing
};

  // Calculate if dot should be visible for a segment
  const shouldShowDot = (segment) => {
    if (!animationStarted || shouldAnimateOut) return false;
    
    // Check if all steps up to the segment's toIndex are visible
    for (let i = 0; i <= segment.toIndex; i++) {
      if (!visibleSteps.includes(i)) return false;
    }
    
    return true;
  };

  // Calculate segment opacity with smooth transitions
  const getSegmentOpacity = (segment) => {
    if (!isSectionInView) return 0;
    
    if (shouldAnimateOut) {
      // When hiding, check if this segment should be hidden based on step visibility
      const fromStepVisible = visibleSteps.includes(segment.fromIndex);
      const toStepVisible = visibleSteps.includes(segment.toIndex);
      
      if (fromStepVisible && toStepVisible) {
        return 0.7; // Both steps visible, keep segment visible
      } else {
        return 0; // One or both steps hidden, hide segment
      }
    }
    
    // Normal showing behavior
    if (segment.isVisible) {
      return 0.7;
    }
    
    // Smooth fade in/out based on step visibility
    const fromStepVisible = visibleSteps.includes(segment.fromIndex);
    const toStepVisible = visibleSteps.includes(segment.toIndex);
    
    if (fromStepVisible && toStepVisible) {
      return 0.7;
    } else if (fromStepVisible || toStepVisible) {
      return 0.3; // Partial visibility
    }
    
    return 0;
  };

  // Calculate row connection opacity
  const getRowConnectionOpacity = (rowConnection) => {
    if (!isSectionInView) return 0;
    
    if (shouldAnimateOut) {
      const leftStepVisible = visibleSteps.includes(rowConnection.leftIndex);
      const rightStepVisible = visibleSteps.includes(rowConnection.rightIndex);
      
      if (leftStepVisible && rightStepVisible) {
        return 0.6; // Both steps visible, keep row connection visible
      } else {
        return 0; // One or both steps hidden, hide row connection
      }
    }
    
    // Normal showing behavior
    if (rowConnection.isVisible) {
      return 0.6;
    }
    
    // Smooth fade in/out based on step visibility
    const leftStepVisible = visibleSteps.includes(rowConnection.leftIndex);
    const rightStepVisible = visibleSteps.includes(rowConnection.rightIndex);
    
    if (leftStepVisible && rightStepVisible) {
      return 0.6;
    } else if (leftStepVisible || rightStepVisible) {
      return 0.2; // Partial visibility
    }
    
    return 0;
  };

  // Get step opacity with hiding support
  const getStepOpacity = (stepIndex) => {
    if (!isSectionInView) return 0;
    
    if (shouldAnimateOut) {
      return visibleSteps.includes(stepIndex) ? 1 : 0;
    }
    
    return visibleSteps.includes(stepIndex) ? 1 : 0;
  };

  return (
    <svg 
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 1,
        pointerEvents: 'none'
      }}
      viewBox={`0 0 ${containerWidth} ${totalHeight}`}
      preserveAspectRatio="none"
    >
      <defs>
        <filter id="mobileGlow">
          <feGaussianBlur stdDeviation="1.5" result="coloredBlur"/>
          <feMerge> 
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
        
        <linearGradient id="lineGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={white} stopOpacity="0.6" />
          <stop offset="50%" stopColor={white} stopOpacity="0.9" />
          <stop offset="100%" stopColor={white} stopOpacity="0.6" />
        </linearGradient>
      </defs>

      {/* ROW CONNECTIONS - Horizontal dashed lines between steps in same row */}
      {rowConnections.map((rowConnection, index) => (
        <path
          key={`row-${rowConnection.rowIndex}`}
          d={rowConnection.d}
          fill="none"
          stroke={white}
          strokeWidth="1"
          opacity={getRowConnectionOpacity(rowConnection)}
          strokearray="2,2"
          // strokeDasharray="2,2"
          style={{
            transition: `all 0.4s ease-in-out ${getStepAnimationDelay(rowConnection.leftIndex, shouldAnimateOut)}s`,
            strokeLinecap: 'round'
          }}
        />
      ))}

      {/* Render connection path segments with progressive animation */}
      {pathSegments.map((segment, index) => (
        <path
          key={`segment-${segment.fromIndex}-${segment.toIndex}`}
          d={segment.d}
          fill="none"
          stroke="url(#lineGradient)"
          strokeWidth="0.8"
          opacity={getSegmentOpacity(segment)}
          strokearray={segment.type === 'line' ? 'none' : '5,3'}
          // strokeDasharray={segment.type === 'line' ? 'none' : '5,3'}
          style={{
            transition: `all 0.4s ease-in-out ${getStepAnimationDelay(segment.toIndex, shouldAnimateOut)}s`,
            strokeLinecap: 'round',
            strokeLinejoin: 'round'
          }}
        />
      ))}

      {/* Animated flowing dots for each visible segment */}
      {pathSegments.map((segment, index) => (
        shouldShowDot(segment) && (
          <React.Fragment key={`dots-${segment.fromIndex}-${segment.toIndex}`}>
            {/* Main flowing dot */}
            <circle
              r="1.2"
              fill={primary}
              opacity={getSegmentOpacity(segment)}
              filter="url(#mobileGlow)"
            >
              <animateMotion
                dur="8s"
                repeatCount="indefinite"
                path={segment.d}
              />
              <animate
                attributeName="r"
                values="1;2;1"
                dur="1.5s"
                repeatCount="indefinite"
              />
            </circle>
            
            {/* Secondary trailing dot */}
            <circle
              r="0.8"
              fill={primary}
              opacity={getSegmentOpacity(segment) * 0.5}
              filter="url(#mobileGlow)"
            >
              <animateMotion
                dur="8s"
                repeatCount="indefinite"
                path={segment.d}
                begin="0.5s"
              />
            </circle>
          </React.Fragment>
        )
      ))}

      {/* Step connection points with progressive visibility */}
      {stepPositions.map((position, index) => (
        <g key={position.step.id || index}>
          {/* Outer glow circle */}
          <circle
            cx={position.x}
            cy={position.y}
            r="2.5"
            fill={primary}
            opacity={getStepOpacity(index) * 0.3}
            style={{
              transition: `all 0.4s ease-in-out ${getStepAnimationDelay(index, shouldAnimateOut)}s`
            }}
          />
          
          {/* Main connection point */}
          <circle
            cx={position.x}
            cy={position.y}
            r="1.5"
            fill={primary}
            opacity={getStepOpacity(index)}
            style={{
              transition: `all 0.4s ease-in-out ${getStepAnimationDelay(index, shouldAnimateOut)}s`,
              filter: 'url(#mobileGlow)'
            }}
          />
          
          {/* Inner white circle */}
          <circle
            cx={position.x}
            cy={position.y}
            r="0.7"
            fill="white"
            opacity={getStepOpacity(index)}
            style={{
              transition: `all 0.4s ease-in-out ${getStepAnimationDelay(index, shouldAnimateOut)}s`
            }}
          />

          {/* Connection point animation when step becomes visible */}
          {position.isVisible && isSectionInView && !shouldAnimateOut && (
            <circle
              cx={position.x}
              cy={position.y}
              r="4"
              fill="none"
              stroke={primary}
              strokeWidth="0.5"
              opacity="0"
              style={{
                animation: `connectionPointPulse 1s ease-out ${getStepAnimationDelay(index)}s`
              }}
            />
          )}
        </g>
      ))}
    </svg>
  );
};

export default ConnectionLines;